import { html } from '@effectivastudio/lit-components';
import BaseMenu from '@effectivastudio/lit-components/components/base_menu/element.js';
import translations from './translations.js';
import '@effectivastudio/lit-components/components/dot_menu/element.js';

import ChatModel from '@effectivastudio/lit-components/components/chat/lib/chat.model.js';

import { StoreController } from '@effectivastudio/lit-components/lib/lit/store_controller.js';
import { computedChatsStore } from '@effectivastudio/lit-components/components/chat/lib/chats.store.js';

import { uuid } from '@effectivastudio/lit-components/lib/helpers.js';

/**
 * The dot-dot-dot menu for chat
 *
 * @prop resourceName - Menu title
 * @prop chatId - The id of the chat
 */

export default class ChatMenu extends BaseMenu {
  /** @internal */
  static translations = translations;

  static properties = {
    chatId: { type: Number },
  };

  storeController = new StoreController(this, computedChatsStore);

  constructor() {
    super();
    this.timestamp = Date.now();
  }

  connectedCallback() {
    super.connectedCallback();
    this.chatModel = new ChatModel(this.chatId);
    this.resourceName = this.chatModel.storedChat?.name;
  }

  get isMuted() {
    return this.chatModel.storedChat?.isMuted;
  }

  get unreadMessagesCount() {
    return this.chatModel.storedChat?.unreadMessagesCount;
  }

  get kind() {
    return this.chatModel.storedChat?.kind;
  }

  get links() {
    return this.chatModel.storedChat?.links || {};
  }

  /** @internal */
  get instanceId() {
    return (this.uuid ??= uuid());
  }

  /** @internal */
  get baseUrl() {
    return `/chats/${this.chatId}`;
  }

  get exitOn() {
    return this.kind !== 'group';
  }

  get muteItem() {
    return {
      title: this.isMuted ? this.t('actions.unmute') : this.t('actions.mute'),
      action: this.actionFor('toggleMute'),
      icon: 'volume',
      partName: 'volume',
    };
  }

  get exitItem() {
    return {
      title: this.t('actions.exit'),
      action: this.actionFor('exitChat'),
      role: 'destructive',
      icon: 'right-from-bracket',
      partName: 'delete destructive',
    };
  }

  get markAllAsReadItem() {
    return {
      title: this.t('actions.mark_all_as_read'),
      action: this.actionFor('markChatAsRead'),
      icon: 'check',
      partName: 'check',
    };
  }

  get seeMembersItem() {
    if (!this.links.members) return;

    return {
      title: this.t('actions.see_members'),
      action: this.actionFor('visit', [this.links.members]),
      icon: 'users',
      partName: 'users',
    };
  }

  get backToGroupItem() {
    if (!this.links.group) return;

    return {
      title: this.t(`actions.back_to_group`),
      action: this.actionFor('visit', [this.links.group]),
      icon: 'home',
      partName: 'home',
    };
  }

  toggleMute() {
    this.chatModel.toggleMute();
  }

  markChatAsRead() {
    this.chatModel.markChatAsRead();
  }

  visit(url) {
    this.commonActions.visit(url);
  }

  async exitChat() {
    if (!this.commonActions.confirmDialog(this.t('are_you_sure'))) return false;

    await this.chatModel.exit();
    const event = new Event('chatMenu-exited', {
      bubbles: true,
      composed: true,
    });
    event.detail = {
      chatId: this.chatModel.id,
    };

    this.dispatchEvent(event);
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get dividerOn() {
    return this.exitOn;
  }

  get markAllAsReadItemOn() {
    return this.unreadMessagesCount > 0;
  }

  /** @internal */
  get items() {
    return [
      this.backToGroupItem,
      this.markAllAsReadItemOn && this.markAllAsReadItem,
      this.seeMembersItem,
      this.muteItem,
      this.dividerOn && this.divider,
      this.exitOn && this.exitItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      header: this.resourceName,
      buttons: this.items,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'xs-chat-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  commentExecJSFromResponse(url) {
    this.commonActions.execJSFromResponse(url);
  }

  render() {
    return html` <ef-dot-menu .config=${this.dotMenuConfig}></ef-dot-menu> `;
  }
}

ChatMenu.registerTagName('xs-chat-menu');
ChatMenu.registerCommonActions('xs-chat-menu');

customElements.get('xs-chat-menu') ||
  customElements.define('xs-chat-menu', ChatMenu);
